import { FunctionComponent, useEffect, useState } from 'react';
import { Div, Gutter, Title, DropdownMultiselect, OptionListOption } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UseFormReturn } from 'react-hook-form';
import { UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { UserGroupEditForm } from '../../Pages/Onboarding/GroupSettings/GroupSettings';
import UserGroupEditFields from './UserGroupEditFields';
import { RootState } from '../../store/reducers';

type UserGroupEditProps = {
  companyName: string;
  formUtils: Omit<UseFormReturn<UserGroupEditForm>, 'handleSubmit'>;
};

const userRoles = Object.values(UserRole) as UserRole[];

const UserGroupEdit: FunctionComponent<UserGroupEditProps> = ({ companyName, formUtils }) => {
  const [groupUserRoleOptions, setGroupUserRoleOptions] = useState<OptionListOption[]>([]);
  const { t, i18n } = useTranslation('globalComponents');
  const selectedUserGroup = useSelector((state: RootState) => state.userGroups.selectedUserGroup);

  const watchDefaultUserRoles = formUtils.watch('default_user_roles');

  useEffect(() => {
    setGroupUserRoleOptions(
      userRoles.map(ur => ({
        label: t(`userRoles.${ur}`),
        value: ur,
        checked: selectedUserGroup?.default_user_roles.includes(ur),
      })),
    );
  }, [selectedUserGroup, t]);

  useEffect(() => {
    if (watchDefaultUserRoles) {
      setGroupUserRoleOptions(
        userRoles.map(ur => ({
          label: t(`userRoles.${ur}`),
          value: ur,
          checked: watchDefaultUserRoles.includes(ur),
        })),
      );
    }
  }, [watchDefaultUserRoles, i18n.language, t]);

  const handleRolesChange = (value: UserRole) => {
    let newUserRoles = [...formUtils.getValues('default_user_roles')];
    if (newUserRoles.includes(value)) {
      newUserRoles = newUserRoles.filter(ur => ur !== value);
    } else {
      newUserRoles.push(value);
    }
    formUtils.setValue('default_user_roles', newUserRoles, { shouldDirty: true });
    formUtils.clearErrors('default_user_roles');
  };

  return (
    <Div padding={{ size: 'md' }} backgroundColor="white">
      {/* Delivery address */}
      <Title size={'xs'} margin={{ bottom: 'md' }}>
        {t('UserGroupEdit.userRightsTitle')}
      </Title>
      <DropdownMultiselect
        showCustomLabel={true}
        onChange={(_event, option) => handleRolesChange(option.value as UserRole)}
        onClear={() => {}}
        options={groupUserRoleOptions}
        errorText={formUtils.formState.errors?.default_user_roles?.[0]?.message}
      />

      <Title size={'xs'} margin={{ top: 'lg', bottom: 'sm' }}>
        {t('UserGroupEdit.deliverAddressTitle')}
      </Title>

      <UserGroupEditFields companyName={companyName} mode={'delivery'} formUtils={formUtils} />

      <Gutter size={'xs'} />
    </Div>
  );
};

export default UserGroupEdit;
